import React, { createContext, useContext, useCallback, useEffect, ReactNode } from 'react';
import { sileo, Toaster } from 'sileo';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastInput {
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

interface ToastContextType {
  showToast: (toast: ToastInput) => void;
  hideToast: (id: string) => void;
  showSuccess: (title: string, message?: string, duration?: number) => void;
  showError: (title: string, message?: string, duration?: number) => void;
  showWarning: (title: string, message?: string, duration?: number) => void;
  showInfo: (title: string, message?: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

function fireToast(type: ToastType, title: string, message?: string, duration?: number) {
  const opts = {
    title,
    ...(message ? { description: message } : {}),
    ...(duration !== undefined ? { duration } : {}),
  };

  switch (type) {
    case 'success':
      return sileo.success(opts);
    case 'error':
      return sileo.error(opts);
    case 'warning':
      return sileo.warning(opts);
    case 'info':
      return sileo.info(opts);
  }
}

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const showToast = useCallback((toast: ToastInput) => {
    fireToast(toast.type, toast.title, toast.message, toast.duration);
  }, []);

  const hideToast = useCallback((id: string) => {
    sileo.dismiss(id);
  }, []);

  const showSuccess = useCallback((title: string, message?: string, duration?: number) => {
    fireToast('success', title, message, duration);
  }, []);

  const showError = useCallback((title: string, message?: string, duration?: number) => {
    fireToast('error', title, message, duration);
  }, []);

  const showWarning = useCallback((title: string, message?: string, duration?: number) => {
    fireToast('warning', title, message, duration);
  }, []);

  const showInfo = useCallback((title: string, message?: string, duration?: number) => {
    fireToast('info', title, message, duration);
  }, []);

  const value: ToastContextType = {
    showToast,
    hideToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  // Click en cualquier toast → cerrar las notificaciones visibles.
  // Sileo no expone el id del toast en el DOM ni trae ✕ de esquina, así que
  // usamos clear() de la posición. El swipe vertical (nativo de Sileo) sigue
  // disponible para cerrar una individual.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest('[data-sileo-toast]')) {
        sileo.clear('top-right');
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  return (
    <ToastContext.Provider value={value}>
      {/* theme fijo en dark: cashinbot deja el default ("system") porque su app tiene
          light/dark, pero acá la UI es siempre oscura y un toast claro desentona. */}
      <Toaster
        position="top-right"
        offset={{ top: 24, right: 24 }}
        theme="dark"
      />
      {children}
    </ToastContext.Provider>
  );
};
